import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import InfoIcon from "@mui/icons-material/Info";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import DateRangeIcon from "@mui/icons-material/DateRange";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import { useSelector, useDispatch } from "react-redux";
import CustomSnackbar from "@Components/Snackbar/Snackbar";
import { setSnackbar } from "@Redux/snackBarSlice";
import { SectionTitle } from "./styles";
import axios from "axios";
import axiosInstance from "../../../API/axios";
import qs from 'qs';
import RequestApproverDialog from "@Components/RequestApproverDialog/RequestApproverDialog";
import { isExemption, getPlanTypeLabel, getPlanStatusLabel } from "@Utils/planType";

export const ExitPlan = ({ member, approvingManagers = [], exitRequestId }) => {
  // if (!member.response.exitPlanStatus || member.response.exitPlanStatus !== "ready") {
  //   return (
  //     <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fff8e1" }}>
  //       <Stack direction="row" spacing={1} alignItems="center">
  //         <HourglassEmptyIcon color="warning" />
  //         <Typography>
  //           Exit plan is currently under review. Details will be available once approved.
  //         </Typography>
  //       </Stack>
  //     </Paper>
  //   );
  // }

  // // Check if additional comments exist and are not empty
  // const hasAdditionalComments = member.response.exitPlan.additionalComments &&
  //                              member.response.exitPlan.additionalComments.trim() !== '';
  const responseId = member.response.id;
  const hasExitPlans =
    member.response.exitPlans && member.response.exitPlans.length > 0;
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.user.userId);

  // Approve dialog state
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [localPlans, setLocalPlans] = useState(member.response.exitPlans || []);
  const [requestApproverOpen, setRequestApproverOpen] = useState(false);

  // Exit plans are knowledge only now: only exemption requests are ever approved here, and only
  // the ones this manager has not already decided on.
  const pendingExemptionsForMe = (() => {
    if (!member?.response || !Array.isArray(member.response.exitPlans)) {
      return [];
    }
    return member.response.exitPlans.filter(
      (plan) =>
        isExemption(plan) &&
        plan.status === "SUBMITTED" &&
        !(plan.approvals || []).some((a) => a.approver?.id === userId)
    );
  })();

  const isManager = approvingManagers.some((mgr) => mgr.id === userId);
  const canShowApproveButton =
    !member?.response?.isCompliant && isManager && pendingExemptionsForMe.length > 0;

  // Exemption requests that this bulk action will not touch, so the dialog can say so.
  const hasOtherNonSubmittedPlans = (() => {
    if (!member?.response || !Array.isArray(member.response.exitPlans)) {
      return false;
    }
    return member.response.exitPlans.some(
      (plan) => isExemption(plan) && plan.status !== "SUBMITTED"
    );
  })();

  // Approve button handler
  const handleApproveClick = () => {
    setApproveDialogOpen(true);
    setApproveComment("");
  };

  // Confirm approve handler
  const handleApproveConfirm = async () => {
    setApproveLoading(true);
    try {
      await axiosInstance.post(
        `/exit-request-response/${member.response.id}/bulk-approve-plans`,
        {
          approvalComment: approveComment,
          excludedPlanIds: [],
          forceApproval: true,
        }
      );
      setApproveDialogOpen(false);

      // Store current state in sessionStorage for after reload
      sessionStorage.setItem('bulkApproveSuccess', 'Exemption requests approved successfully.');

      // Create query params to restore current state after reload
      const currentParams = {
        tab: 'responses',
        selectedMemberId: member.id,
        showExitPlan: 'true'
      };

      const queryString = qs.stringify(currentParams, { addQueryPrefix: true });

      // Reload with query params to restore state
      window.location.href = window.location.pathname + queryString;
    } catch (err) {
      console.log("Bulk approve error:", err);
      dispatch(setSnackbar({
        open: true,
        message: "Failed to approve exemption requests.",
        error: true,
      }));
      setApproveDialogOpen(false);
    } finally {
      setApproveLoading(false);
    }
  };

  const navigate = useNavigate();

  // Check for success message in sessionStorage on component mount
  useEffect(() => {
    const successMessage = sessionStorage.getItem('bulkApproveSuccess');
    if (successMessage) {
      dispatch(setSnackbar({
        open: true,
        message: successMessage,
        error: false,
      }));
      // Remove the message from sessionStorage after displaying
      sessionStorage.removeItem('bulkApproveSuccess');
    }
  }, [dispatch]);

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <SectionTitle variant="h6">Exit Plans &amp; Exemption Requests</SectionTitle>
      {localPlans && localPlans.length > 0 ? (
        <Box sx={{ flexGrow: 1, overflow: "auto", pb: 2 }}>
          <Paper variant="outlined" sx={{ mb: 2 }}>
            {localPlans.map((plan, idx) => (
              <React.Fragment key={`${plan.id}-${idx}`}>
                {idx > 0 && <Divider />}
                <Box sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {getPlanTypeLabel(plan) + " #" + (idx + 1)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Box display="flex" alignItems="center">
                        <DateRangeIcon
                          fontSize="small"
                          color="primary"
                          sx={{ mr: 0.75 }}
                        />
                        <Typography variant="body2" noWrap fontWeight="bold">
                          {"Created at: "}
                          {plan.createdAt
                            ? dayjs(plan.createdAt).format("MM/DD/YYYY")
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Typography variant="subtitle1">
                        <Box component="span" fontWeight="bold">
                          Status:
                        </Box>{" "}
                        {getPlanStatusLabel(plan.status, plan.planType)}
                      </Typography>
                      {isExemption(plan) && plan.totalApprovers > 0 && plan.status === "SUBMITTED" && (
                        <Typography variant="caption" color="textSecondary">
                          {plan.approvedCount}/{plan.totalApprovers} approved
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box display="flex" sx={{ pl: 8 }}>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => {
                            navigate(
                              DefaultRoutes.ViewExitPlan
                                .replace(":responseId", responseId),
                              { state: { planId: plan.id } }
                            );
                          }}
                        >
                          View Details
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </React.Fragment>
            ))}
          </Paper>
          {/* Approve Exit Plans Button - always below the list, never overlay */}
          {canShowApproveButton && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 2 }}>
              <Button
                variant="outlined"
                color="warning"
                startIcon={<GroupAddIcon />}
                onClick={() => setRequestApproverOpen(true)}
                sx={{ minWidth: 200, fontWeight: "bold" }}
              >
                Add Approver
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveClick}
                sx={{ minWidth: 180, fontWeight: "bold" }}
              >
                Approve Exemption Requests
              </Button>
            </Box>
          )}
          {/* Approve Confirmation Dialog */}
          <Dialog
            open={approveDialogOpen}
            onClose={() => setApproveDialogOpen(false)}
            PaperProps={{
              sx: {
                minWidth: 600,
                maxWidth: 800,
              }
            }}
          >
            <DialogTitle>Approve Submitted Exemption Requests</DialogTitle>
            <DialogContent>
              <Typography gutterBottom>
                Are you sure you want to approve the {pendingExemptionsForMe.length} submitted
                exemption request(s) awaiting your decision on this impact assessment?
              </Typography>
              {hasOtherNonSubmittedPlans && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Exit plans need no approval, and exemption requests not yet submitted are
                  unaffected by this action.
                </Alert>
              )}
              <TextField
                label="Approval Comment (optional)"
                fullWidth
                multiline
                minRows={6}
                value={approveComment}
                onChange={(e) => setApproveComment(e.target.value)}
                sx={{ mt: 2, minHeight: 180 }}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setApproveDialogOpen(false)} disabled={approveLoading}>Cancel</Button>
              <Button
                variant="contained"
                color="success"
                onClick={handleApproveConfirm}
                disabled={approveLoading}
                startIcon={approveLoading ? <CircularProgress size={20} /> : null}
              >
                {approveLoading ? "Approving..." : "Confirm Approval"}
              </Button>
            </DialogActions>
          </Dialog>
          <CustomSnackbar />
          <RequestApproverDialog
            open={requestApproverOpen}
            onClose={() => setRequestApproverOpen(false)}
            exitRequestId={exitRequestId}
            onSaved={() => {
              setRequestApproverOpen(false);
              dispatch(setSnackbar({ open: true, message: 'Approver added successfully.', error: false }));
            }}
          />
        </Box>
      ) : (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "#fff8e1" }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <HourglassEmptyIcon color="warning" />
            <Typography>
              No exit plans or exemption requests have been created yet. They will appear
              here once the R&D member submits them.
            </Typography>
          </Stack>
        </Paper>
      )}
    </Box>
  );
};
